'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  Bell,
  X,
  Check,
  CheckCheck,
  Trash2,
  Clock,
  AlertCircle,
  MessageSquare,
  UserPlus,
  Tag,
  AlertTriangle,
  Calendar,
  AtSign,
  CircleAlert,
  CheckSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiFetch } from '@/lib/apiFetch';
import { isAppShellRoute } from '@/lib/platformUtils';
import { SectionLoader } from '@/components/shared/BrandedLoadingSpinner';
import { useBackgroundAwareInterval } from '@/hooks/useBackgroundAwareInterval';
import { useBadge } from '@/contexts/BadgeContext';
import { useTheme } from '@/contexts/ThemeContext';
import { dispatchOpenWorkItem } from '@/hooks/useWorkItemOpener';
import NotificationDetailSlideOver, { type InboxItem } from './NotificationDetailSlideOver';
import {
  useAttentionInbox,
  getApprovalDisplay,
  MYHR_LEAVE_APPROVAL_LINK,
  type ManagedApprovalRequest,
  type PendingTagNotification,
} from './useAttentionInbox';

export interface Notification {
  id: string;
  work_item_id: string | null;
  notification_type: string;
  title: string;
  message: string | null;
  link: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  metadata: Record<string, unknown>;
  work_item?: {
    id: string;
    title: string;
    item_type: string;
    status: string;
    item_number: string;
  } | null;
}

interface NotificationBellProps {
  className?: string;
  /** Extra classes for the bell <button> (match surrounding icon buttons). */
  buttonClassName?: string;
  /** Extra classes for the bell icon itself (match surrounding icon sizes). */
  iconClassName?: string;
  onNavigate?: (link: string) => void;
  /** Opens a message thread (mention → open conversation). */
  onOpenThread?: (threadId: string) => void;
  /** Opens the Managed Tasks workspace (tasks-due → "Open Managed Tasks"). */
  onOpenManagedTasks?: () => void;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'assigned_to_you':
      return <UserPlus className="h-4 w-4 text-blue-500" />;
    case 'mentioned_in_comment':
    case 'comment_added':
      return <MessageSquare className="h-4 w-4 text-purple-500" />;
    case 'status_changed':
      return <Check className="h-4 w-4 text-green-500" />;
    case 'due_date_approaching':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'due_date_overdue':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'sla_warning':
      return <AlertCircle className="h-4 w-4 text-orange-500" />;
    case 'sla_breached':
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    case 'tag_added':
      return <Tag className="h-4 w-4 text-indigo-500" />;
    case 'priority_changed':
      return <AlertCircle className="h-4 w-4 text-amber-500" />;
    default:
      return <Bell className="h-4 w-4 text-gray-500" />;
  }
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

function SectionHeader({ label }: { label: string }) {
  return (
    <p className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
      {label}
    </p>
  );
}

export function NotificationBell({
  className,
  buttonClassName,
  iconClassName,
  onNavigate,
  onOpenThread,
  onOpenManagedTasks,
}: NotificationBellProps) {
  const router = useRouter();
  const { badgeCounts } = useBadge();
  const { clientBranding, currentTheme } = useTheme();
  const accent = clientBranding?.primary_color || currentTheme.colors.primary;

  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [selectedItem, setSelectedItem] = useState<InboxItem | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const inbox = useAttentionInbox();

  const attentionCount =
    badgeCounts.pendingMentionsCount
    + badgeCounts.pendingApprovalsCount
    + badgeCounts.tasksDueTodayCount;
  const totalBadge = unreadCount + attentionCount;

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: '20',
        ...(filter === 'unread' && { unread_only: 'true' })
      });

      const response = await apiFetch(`/api/v1/notifications?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch notifications');

      const data = await response.json();
      if (data.success) {
        setNotifications(data.data || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Fetch on open — notifications feed plus the attention sections split out
  // of ManagedTasksModal (mentions, approvals, tasks due).
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      void inbox.refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fetchNotifications]);

  // Allow other chrome (e.g. the mobile menu's Notifications entry) to open
  // the panel without prop-drilling open state through the header.
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('ta:open-notifications', handler);
    return () => window.removeEventListener('ta:open-notifications', handler);
  }, []);

  // Poll for unread count (background-aware: pauses while app is backgrounded,
  // fires immediately on resume to keep the badge fresh).
  useBackgroundAwareInterval(async () => {
    // The bell only matters inside the app shell — skip the poll on public
    // pages so a stale session doesn't produce a 401 every interval.
    if (typeof window !== 'undefined' && !isAppShellRoute(window.location.pathname)) {
      return;
    }
    try {
      const response = await apiFetch('/api/v1/notifications?limit=1', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      // Silent fail for polling
    }
  }, {
    intervalMs: 30000,
    runImmediately: true,
    fireOnResume: true,
  });

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId: string) => {
    try {
      await apiFetch('/api/v1/notifications', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'mark_read',
          notification_id: notificationId
        })
      });

      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiFetch('/api/v1/notifications', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'mark_all_read' })
      });

      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await apiFetch(`/api/v1/notifications?id=${notificationId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const closeAll = () => {
    setSelectedItem(null);
    setIsOpen(false);
  };

  const handleOpenMention = useCallback((mention: PendingTagNotification) => {
    closeAll();
    if (mention.source === 'work_item_comment' && mention.work_item_id) {
      dispatchOpenWorkItem({ id: mention.work_item_id });
      return;
    }

    if (onOpenThread) {
      onOpenThread(mention.thread_id);
      return;
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('openMessagesModal', { detail: { threadId: mention.thread_id } }));
    }
  }, [onOpenThread]);

  const handleOpenApproval = useCallback((request: ManagedApprovalRequest) => {
    closeAll();
    const suffix = typeof request.id === 'number' ? `&request_id=${request.id}` : '';
    const targetUrl = request.approval_type === 'device_admin'
      ? '/client-zone/admin-hub?tab=iam'
      : request.approval_type === 'leave'
      ? `${MYHR_LEAVE_APPROVAL_LINK}${suffix}`
      : request.approval_type === 'shift_violation'
        ? `/client-zone/modules/total-attendance?tab=violations${suffix}`
        : `/client-zone/modules/total-attendance?tab=approvals${suffix}`;
    router.push(targetUrl);
  }, [router]);

  const handleOpenWorkItem = useCallback((workItemId: string) => {
    closeAll();
    dispatchOpenWorkItem({ id: workItemId });
  }, []);

  const handleOpenLink = useCallback((link: string) => {
    closeAll();
    onNavigate?.(link);
  }, [onNavigate]);

  const handleItemClick = (item: InboxItem) => {
    if (item.kind === 'notification' && !item.notification.is_read) {
      markAsRead(item.notification.id);
    }
    setSelectedItem(item);
  };

  const visibleNotifications = filter === 'unread'
    ? notifications.filter((n) => !n.is_read)
    : notifications;

  const showMentions = inbox.mentions.length > 0;
  const showApprovals = inbox.approvals.length > 0;
  const showDueTasks = inbox.dueTasks.length > 0;
  const isEmpty =
    !loading
    && !inbox.mentionsLoading
    && !inbox.approvalsLoading
    && !inbox.tasksLoading
    && !showMentions
    && !showApprovals
    && !showDueTasks
    && visibleNotifications.length === 0;
  const isLoading = loading || (inbox.mentionsLoading && inbox.approvalsLoading && inbox.tasksLoading);

  return (
    <div className={cn('relative', className)}>
      {/* Bell Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative p-2 rounded-lg transition-colors',
          'hover:bg-gray-100 dark:hover:bg-gray-800',
          isOpen && 'bg-gray-100 dark:bg-gray-800',
          buttonClassName,
        )}
        title="Notifications"
        aria-label="Notifications"
      >
        <Bell className={cn('h-5 w-5 text-gray-600 dark:text-gray-400', iconClassName)} />
        {totalBadge > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {totalBadge > 99 ? '99+' : totalBadge}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className={cn(
            'absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-1rem)] max-h-[500px] overflow-hidden',
            'bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700',
            'z-50 flex flex-col'
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setFilter('all')}
              className={cn(
                'flex-1 py-2 text-sm font-medium transition-colors',
                filter === 'all'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={cn(
                'flex-1 py-2 text-sm font-medium transition-colors',
                filter === 'unread'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Unread {totalBadge > 0 && `(${totalBadge})`}
            </button>
          </div>

          {/* Notification List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <SectionLoader size="xs" primaryColor="#2563eb" className="py-8" />
            ) : isEmpty ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Bell className="h-10 w-10 mb-3 opacity-30" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <div>
                {/* Approvals awaiting action */}
                {showApprovals && (
                  <>
                    <SectionHeader label="To approve" />
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {inbox.approvals.map((request) => {
                        const display = getApprovalDisplay(request, accent);
                        return (
                          <div
                            key={`approval-${request.approval_type}-${request.id}`}
                            className="relative group px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 bg-amber-50/40 dark:bg-amber-900/10"
                            onClick={() => handleItemClick({ kind: 'approval', approval: request })}
                          >
                            <div className="flex gap-3">
                              <div className="flex-shrink-0 mt-0.5">
                                <CircleAlert className="h-4 w-4 text-amber-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">
                                    {request.employee_name}
                                    <span className="ml-1.5 font-normal text-gray-500 dark:text-gray-400 capitalize">
                                      {display.requestLabel}
                                    </span>
                                  </p>
                                  <span className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-amber-500" />
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                                  {display.summary}
                                </p>
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                  {formatTimeAgo(request.requested_at)}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Pending mentions */}
                {showMentions && (
                  <>
                    <SectionHeader label="Mentions" />
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {inbox.mentions.map((mention) => (
                        <div
                          key={`mention-${mention.thread_id}-${mention.message_id}`}
                          className="relative group px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 bg-purple-50/40 dark:bg-purple-900/10"
                          onClick={() => handleItemClick({ kind: 'mention', mention })}
                        >
                          <div className="flex gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              <AtSign className="h-4 w-4 text-purple-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">
                                  {mention.thread_name}
                                </p>
                                <span className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-purple-500" />
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                                <span className="font-medium">{mention.sender_name}</span>
                                {' · '}
                                {mention.message_preview}
                              </p>
                              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                {formatTimeAgo(mention.timestamp)}
                              </p>
                            </div>
                          </div>

                          <div className="absolute top-2 right-2 hidden group-hover:flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                void inbox.dismissMentions([mention]);
                              }}
                              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                              title="Dismiss mention"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-gray-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Tasks due today or overdue */}
                {showDueTasks && (
                  <>
                    <SectionHeader label="Tasks due" />
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {inbox.dueTasks.map((task) => {
                        const due = task.due_date ? new Date(task.due_date) : null;
                        const todayStart = new Date();
                        todayStart.setHours(0, 0, 0, 0);
                        const overdue = due ? due.getTime() < todayStart.getTime() : false;
                        return (
                          <div
                            key={`task-${task.id}`}
                            className="relative group px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 bg-red-50/40 dark:bg-red-900/10"
                            onClick={() => handleItemClick({ kind: 'task', task })}
                          >
                            <div className="flex gap-3">
                              <div className="flex-shrink-0 mt-0.5">
                                <CheckSquare className={cn('h-4 w-4', overdue ? 'text-red-500' : 'text-blue-500')} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">
                                    {task.item_number}
                                    <span className="ml-1.5 font-normal text-gray-500 dark:text-gray-400">
                                      {task.title}
                                    </span>
                                  </p>
                                  <span className={cn('flex-shrink-0 w-2 h-2 mt-1.5 rounded-full', overdue ? 'bg-red-500' : 'bg-blue-500')} />
                                </div>
                                <p className={cn('text-xs mt-0.5', overdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400')}>
                                  {due ? (overdue ? `Overdue since ${formatTimeAgo(due.toISOString())}` : 'Due today') : 'Due'}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Notification feed */}
                {visibleNotifications.length > 0 && (
                  <>
                    <SectionHeader label="Notifications" />
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {visibleNotifications.map(notification => (
                        <div
                          key={notification.id}
                          className={cn(
                            'relative group px-4 py-3 cursor-pointer transition-colors',
                            'hover:bg-gray-50 dark:hover:bg-gray-800/50',
                            !notification.is_read && 'bg-blue-50/50 dark:bg-blue-900/10'
                          )}
                          onClick={() => handleItemClick({ kind: 'notification', notification })}
                        >
                          <div className="flex gap-3">
                            {/* Icon */}
                            <div className="flex-shrink-0 mt-0.5">
                              {getNotificationIcon(notification.notification_type)}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className={cn(
                                  'text-sm line-clamp-2',
                                  !notification.is_read
                                    ? 'font-medium text-gray-900 dark:text-white'
                                    : 'text-gray-700 dark:text-gray-300'
                                )}>
                                  {notification.title}
                                </p>
                                {!notification.is_read && (
                                  <span className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-600" />
                                )}
                              </div>

                              {notification.message && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                                  {notification.message}
                                </p>
                              )}

                              {notification.work_item && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                                  <span className="font-medium">{notification.work_item.item_number}</span>
                                  <span>•</span>
                                  <span className="truncate">{notification.work_item.title}</span>
                                </p>
                              )}

                              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                                {formatTimeAgo(notification.created_at)}
                              </p>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="absolute top-2 right-2 hidden group-hover:flex items-center gap-1">
                            {!notification.is_read && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification.id);
                                }}
                                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                                title="Mark as read"
                              >
                                <Check className="h-3.5 w-3.5 text-gray-500" />
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-gray-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {onOpenManagedTasks && (
            <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-2">
              <button
                onClick={() => {
                  onOpenManagedTasks();
                  setIsOpen(false);
                }}
                className="w-full text-center text-sm text-blue-600 dark:text-blue-400 hover:underline py-1"
              >
                Open Managed Tasks
              </button>
            </div>
          )}
        </div>
      )}

      {/* Detail slide-over for the selected item */}
      <NotificationDetailSlideOver
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        onOpenMention={handleOpenMention}
        onDismissMention={(mention) => {
          void inbox.dismissMentions([mention]).then(() => setSelectedItem(null));
        }}
        onApprove={(request) => {
          void inbox.approveRequest(request).then(() => setSelectedItem(null));
        }}
        onReject={(request) => {
          void inbox.rejectRequest(request).then(() => setSelectedItem(null));
        }}
        onOpenApproval={handleOpenApproval}
        approvalActionPending={
          selectedItem?.kind === 'approval'
            ? inbox.approvalActionIds.has(selectedItem.approval.id)
            : false
        }
        onOpenWorkItem={handleOpenWorkItem}
        onOpenLink={handleOpenLink}
      />
    </div>
  );
}

export default NotificationBell;
